import { bus, destroyApp, startApp } from 'wujie';
import type { PortalApplication, PortalIdentity } from '../model/portalModels';
import { permissionContextForToken } from '../modules/auth/accessTokenManager';
import type {
  FlowPageBridge,
  FlowPageSaveResult,
} from '@jukun/platform-sdk/shell';
import type { PlatformAccessToken } from '@jukun/platform-sdk/auth';
import type {
  BusinessOperationCloseRequest,
  BusinessOperationCompletionResult,
  OperationInitializationFailure,
  RuntimePrimaryNavigationItem,
} from '@jukun/platform-sdk/auth';
import { PLATFORM_PRIMARY_NAVIGATION_SELECT_EVENT } from '@jukun/platform-sdk/shell';
import type { FormRuntimePurpose, PlatformFormSessionHostCapability } from '@jukun/platform-sdk/shell';
import type {
  ProcessInitiationViewContext,
  ProcessInitiationViewReadyReport,
  WorkflowPageContext,
  WorkflowPageFailure,
  WorkflowPageReadyReport,
  WorkflowWorkItemBridge,
} from '@jukun/platform-sdk/workflow';
import {
  NavigationRuntimeRegistry,
  type NavigationRuntimeCallbacks,
  type StableNavigationRuntimeCallbacks,
} from './navigationRuntimeRegistry';

export const WUJIE_ROUTE_CHANGE_EVENT = 'platform:route-change';

export type WujieRouteChange = {
  basePath: string;
  path: string;
};

export type PortalOperationPageBridge = FlowPageBridge;
export type PortalOperationPageResult = FlowPageSaveResult;

export type PortalOperationFeedback = {
  type: 'success' | 'info' | 'warning' | 'error';
  content: string;
  id: string;
  duration: number;
};

export type WujieRuntimeProps = {
  runtimePurpose?: FormRuntimePurpose;
  formSessionHost?: PlatformFormSessionHostCapability;
  runtimeMode: 'embedded';
  applicationCode: string;
  apiBaseUrl: string;
  basePath: string;
  actorPublicId: string;
  actorName: string;
  tenantPublicId: string;
  tenantName: string;
  platformOperator: boolean;
  permissionCodes: string[];
  accessToken: PlatformAccessToken;
  refreshAccessToken: () => Promise<PlatformAccessToken>;
  registerPrimaryNavigation: (
    items: readonly RuntimePrimaryNavigationItem[],
  ) => void;
  reportOperationFeedback: (feedback: PortalOperationFeedback) => void;
  navigate: (path: string) => void;
  onPrimaryNavigationChange: (navigationCode: string) => void;
  onSessionExpired: () => void;
  operationPageRoute?: string;
  reportOperationRouteReady?: (pageRoute: string) => void;
  reportOperationRouteFailed?: (
    failure: OperationInitializationFailure,
  ) => void;
  registerOperationPageBridge?: (
    bridge?: PortalOperationPageBridge,
  ) => void;
  operationInvocationId?: string;
  reportOperationCompleted?: (
    result: BusinessOperationCompletionResult,
  ) => void;
  requestOperationClose?: (
    request: BusinessOperationCloseRequest,
  ) => void;
  workflowPageRoute?: string;
  workflowPageContext?: WorkflowPageContext;
  processInitiationViewPageRoute?: string;
  processInitiationViewContext?: ProcessInitiationViewContext;
  reportProcessInitiationViewReady?: (report: ProcessInitiationViewReadyReport) => void;
  reportProcessInitiationViewFailed?: (failure: WorkflowPageFailure) => void;
  reportWorkflowPageReady?: (report: WorkflowPageReadyReport) => void;
  reportWorkflowPageFailed?: (failure: WorkflowPageFailure) => void;
  registerWorkflowPageBridge?: (bridge?: WorkflowWorkItemBridge) => void;
};

export type RuntimePurpose = 'navigation' | 'transient';

type RuntimeCallbacks = NavigationRuntimeCallbacks<
  PlatformAccessToken,
  PortalOperationFeedback,
  PortalOperationPageBridge
>;

const navigationRuntimeRegistry = new NavigationRuntimeRegistry<
  PlatformAccessToken,
  PortalOperationFeedback,
  PortalOperationPageBridge
>();

export const WUJIE_START_TIMEOUT_MS = 10_000;
export const WUJIE_DESTROY_TIMEOUT_MS = 2_000;

const applicationGenerations = new Map<string, number>();
const applicationQueues = new Map<string, Promise<void>>();
const pendingRuntimeReclaims = new Map<string, {
  runtimeName: string;
  settled: Promise<void>;
}>();
const recoveryBlockedApplications = new Set<string>();

function createAbortError(message = '微前端宿主已取消启动') {
  return new DOMException(message, 'AbortError');
}

function throwIfAborted(signal: AbortSignal) {
  if (signal.aborted) throw createAbortError();
}

function timeoutPromise<T>(timeoutMs: number, value: T) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return {
    promise: new Promise<T>(resolve => {
      timeoutId = setTimeout(() => resolve(value), timeoutMs);
    }),
    clear: () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    },
  };
}

async function destroyRuntimeWithinBudget(
  applicationCode: string,
  runtimeName: string,
) {
  const currentPending = pendingRuntimeReclaims.get(applicationCode);
  if (currentPending?.runtimeName === runtimeName) return false;

  const rawDestroy = Promise.resolve().then(() => destroyApp(runtimeName));
  const destroySettled = rawDestroy.then(() => undefined, () => undefined);
  const destroyOutcome = rawDestroy.then(
    () => 'destroyed' as const,
    () => 'failed' as const,
  );
  const timeout = timeoutPromise(WUJIE_DESTROY_TIMEOUT_MS, 'timeout' as const);
  const outcome = await Promise.race([
    destroyOutcome,
    timeout.promise,
  ]);
  timeout.clear();
  if (outcome === 'destroyed') return true;
  if (outcome === 'failed') {
    recoveryBlockedApplications.add(applicationCode);
    console.error('微前端运行时销毁失败，已停止继续启动', {
      applicationCode,
      runtimeName,
    });
    return false;
  }

  if (currentPending && currentPending.runtimeName !== runtimeName) {
    recoveryBlockedApplications.add(applicationCode);
    console.error('微前端运行时连续产生未回收实例，已停止继续启动', {
      applicationCode,
      pendingRuntimeName: currentPending.runtimeName,
      runtimeName,
    });
    return false;
  }

  const reclaim = { runtimeName, settled: destroySettled };
  pendingRuntimeReclaims.set(applicationCode, reclaim);
  void destroySettled.finally(() => {
    if (pendingRuntimeReclaims.get(applicationCode) === reclaim) {
      pendingRuntimeReclaims.delete(applicationCode);
    }
  });
  return false;
}

function assertNavigationRuntimeStartAllowed(applicationCode: string) {
  if (recoveryBlockedApplications.has(applicationCode)) {
    throw new Error(
      `微前端应用 ${applicationCode} 回收失败，已停止继续启动`,
    );
  }
  if (pendingRuntimeReclaims.has(applicationCode)) {
    throw new Error(
      `微前端应用 ${applicationCode} 仍在回收，暂不允许重新启动`,
    );
  }
}

function resolvePhysicalRuntimeName(applicationCode: string, mountId: string) {
  if (recoveryBlockedApplications.has(applicationCode)) {
    throw new Error(
      `微前端应用 ${applicationCode} 存在多个未回收实例，已停止继续启动`,
    );
  }
  return `PORTAL_TRANSIENT::${applicationCode}::${mountId}`;
}

function onlyWhileMounted<TArgs extends unknown[]>(
  signal: AbortSignal,
  callback: (...args: TArgs) => void,
) {
  return (...args: TArgs) => {
    if (!signal.aborted) callback(...args);
  };
}

async function onlyWhileMountedAsync<T>(
  signal: AbortSignal,
  callback: () => Promise<T>,
) {
  throwIfAborted(signal);
  const result = await callback();
  throwIfAborted(signal);
  return result;
}

function nextApplicationGeneration(applicationCode: string) {
  const generation = (applicationGenerations.get(applicationCode) ?? 0) + 1;
  applicationGenerations.set(applicationCode, generation);
  return generation;
}

function isCurrentGeneration(applicationCode: string, generation: number) {
  return applicationGenerations.get(applicationCode) === generation;
}

function enqueueApplicationOperation<T>(
  applicationCode: string,
  operation: () => Promise<T>,
): Promise<T> {
  const previous = applicationQueues.get(applicationCode) ?? Promise.resolve();
  const run = previous.catch(() => undefined).then(operation);
  const tail = run.then(() => undefined, () => undefined);
  applicationQueues.set(applicationCode, tail);
  return run.finally(() => {
    if (applicationQueues.get(applicationCode) === tail) {
      applicationQueues.delete(applicationCode);
    }
  });
}

export function resolveTrustedAppEntryUrl(
  application: PortalApplication,
  currentHref = window.location.href,
) {
  const base = new URL(application.entry.embedUrl, currentHref);
  if (!['http:', 'https:'].includes(base.protocol)) {
    throw new Error(`微前端入口协议不受支持：${base.protocol}`);
  }
  return base.href;
}

export function resolveTrustedAppUrl(
  application: PortalApplication,
  pathname: string,
  currentHref = window.location.href,
) {
  const base = new URL(resolveTrustedAppEntryUrl(
    application, currentHref,
  ));
  const queryIndex = pathname.indexOf('?');
  const routeOnly = queryIndex >= 0 ? pathname.slice(0, queryIndex) : pathname;
  const routeSearch = queryIndex >= 0 ? pathname.slice(queryIndex) : '';
  const internalPath = routeOnly.startsWith(application.entry.basePath)
    ? routeOnly.slice(application.entry.basePath.length)
    : application.entry.defaultRoute;
  const normalizedRoute = internalPath || application.entry.defaultRoute;
  const routePath = normalizedRoute.startsWith('/')
    ? normalizedRoute : `/${normalizedRoute}`;
  const entryPath = base.pathname === '/'
    ? '' : base.pathname.replace(/\/+$/, '');
  base.pathname = routePath === entryPath || routePath.startsWith(`${entryPath}/`)
    ? routePath
    : `${entryPath}${routePath}`;
  base.search = routeSearch;
  base.hash = '';
  return base.href;
}

export type StartRegisteredApplicationArgs = {
  application: PortalApplication;
  mountId: string;
  signal: AbortSignal;
  runtimePurpose: RuntimePurpose;
  sessionEpoch?: string;
  entryFingerprint?: string;
  identity: PortalIdentity;
  pathname: string;
  container: HTMLElement;
  accessToken: PlatformAccessToken;
  refreshAccessToken: () => Promise<PlatformAccessToken>;
  registerPrimaryNavigation: (
    items: readonly RuntimePrimaryNavigationItem[],
  ) => void;
  navigate: (path: string) => void;
  onPrimaryNavigationChange: (navigationCode: string) => void;
  onSessionExpired: () => void;
  reportOperationFeedback: (feedback: PortalOperationFeedback) => void;
  operationRuntime?: {
    pageRoute: string;
    reportRouteReady: (pageRoute: string) => void;
    reportRouteFailed?: (
      failure: OperationInitializationFailure,
    ) => void;
    registerPageBridge?: (
      bridge?: PortalOperationPageBridge,
    ) => void;
    operationInvocationId?: string;
    reportCompleted?: (
      result: BusinessOperationCompletionResult,
    ) => void;
    requestClose?: (
      request: BusinessOperationCloseRequest,
    ) => void;
  };
  workflowRuntime?: {
    pageRoute: string;
    context: WorkflowPageContext;
    reportReady: (report: WorkflowPageReadyReport) => void;
    reportFailed: (failure: WorkflowPageFailure) => void;
    registerBridge: (bridge?: WorkflowWorkItemBridge) => void;
  };
  processInitiationViewRuntime?: {
    pageRoute: string;
    context: ProcessInitiationViewContext;
    reportReady: (report: ProcessInitiationViewReadyReport) => void;
    reportFailed: (failure: WorkflowPageFailure) => void;
  };
};

function mountedCallbacks(args: StartRegisteredApplicationArgs): RuntimeCallbacks {
  return {
    refreshAccessToken: () => onlyWhileMountedAsync(
      args.signal,
      args.refreshAccessToken,
    ),
    registerPrimaryNavigation: onlyWhileMounted(
      args.signal,
      args.registerPrimaryNavigation,
    ),
    reportOperationFeedback: onlyWhileMounted(
      args.signal,
      args.reportOperationFeedback,
    ),
    navigate: onlyWhileMounted(args.signal, args.navigate),
    onPrimaryNavigationChange: onlyWhileMounted(
      args.signal,
      args.onPrimaryNavigationChange,
    ),
    onSessionExpired: onlyWhileMounted(args.signal, args.onSessionExpired),
    reportOperationRouteReady: args.operationRuntime?.reportRouteReady
      ? onlyWhileMounted(args.signal, args.operationRuntime.reportRouteReady)
      : undefined,
    reportOperationRouteFailed: args.operationRuntime?.reportRouteFailed
      ? failure => {
        if (!args.signal.aborted) {
          args.operationRuntime?.reportRouteFailed?.(
            failure as OperationInitializationFailure,
          );
        }
      }
      : undefined,
    registerOperationPageBridge: args.operationRuntime?.registerPageBridge
      ? onlyWhileMounted(args.signal, args.operationRuntime.registerPageBridge)
      : undefined,
    reportOperationCompleted: args.operationRuntime?.reportCompleted
      ? onlyWhileMounted(args.signal, args.operationRuntime.reportCompleted)
      : undefined,
    requestOperationClose: args.operationRuntime?.requestClose
      ? onlyWhileMounted(args.signal, args.operationRuntime.requestClose)
      : undefined,
    reportWorkflowPageReady: args.workflowRuntime?.reportReady
      ? onlyWhileMounted(args.signal, args.workflowRuntime.reportReady)
      : undefined,
    reportWorkflowPageFailed: args.workflowRuntime?.reportFailed
      ? onlyWhileMounted(args.signal, args.workflowRuntime.reportFailed)
      : undefined,
    registerWorkflowPageBridge: args.workflowRuntime?.registerBridge
      ? onlyWhileMounted(args.signal, args.workflowRuntime.registerBridge)
      : undefined,
  };
}

function runtimeProps(
  args: StartRegisteredApplicationArgs,
  callbacks: StableNavigationRuntimeCallbacks<
    PlatformAccessToken,
    PortalOperationFeedback,
    PortalOperationPageBridge
  >,
): WujieRuntimeProps {
  const props: WujieRuntimeProps = {
    runtimeMode: 'embedded',
    applicationCode: args.application.appCode,
    apiBaseUrl: window.location.origin,
    basePath: args.application.entry.basePath,
    actorPublicId: args.identity.actorPublicId,
    actorName: args.identity.actorName,
    tenantPublicId: args.identity.tenantPublicId,
    tenantName: args.identity.tenantName,
    platformOperator: args.identity.platformOperator,
    permissionCodes: [...permissionContextForToken(args.accessToken).permissionCodes],
    accessToken: args.accessToken,
    refreshAccessToken: callbacks.refreshAccessToken,
    registerPrimaryNavigation: callbacks.registerPrimaryNavigation,
    reportOperationFeedback: callbacks.reportOperationFeedback,
    navigate: callbacks.navigate,
    onPrimaryNavigationChange: callbacks.onPrimaryNavigationChange,
    onSessionExpired: callbacks.onSessionExpired,
  };

  // 稳定代理可能包装未声明的回调，操作能力以原始宿主参数为准。
  const operationRuntime = args.operationRuntime;
  if (operationRuntime) {
    props.operationPageRoute = operationRuntime.pageRoute;
    props.reportOperationRouteReady = callbacks.reportOperationRouteReady;
    if (operationRuntime.registerPageBridge) {
      props.registerOperationPageBridge = callbacks.registerOperationPageBridge;
    }
    if (operationRuntime.reportRouteFailed) {
      props.reportOperationRouteFailed = callbacks.reportOperationRouteFailed as
        (failure: OperationInitializationFailure) => void;
    }
    if (operationRuntime.operationInvocationId !== undefined) {
      props.operationInvocationId = operationRuntime.operationInvocationId;
    }
    if (operationRuntime.reportCompleted) {
      props.reportOperationCompleted = callbacks.reportOperationCompleted;
    }
    if (operationRuntime.requestClose) {
      props.requestOperationClose = callbacks.requestOperationClose;
    }
  }

  const workflowRuntime = args.workflowRuntime;
  if (workflowRuntime) {
    props.workflowPageRoute = workflowRuntime.pageRoute;
    props.workflowPageContext = workflowRuntime.context;
    props.reportWorkflowPageReady = callbacks.reportWorkflowPageReady;
    props.reportWorkflowPageFailed = callbacks.reportWorkflowPageFailed;
    props.registerWorkflowPageBridge = callbacks.registerWorkflowPageBridge;
  }
  if (args.processInitiationViewRuntime) {
    const view = args.processInitiationViewRuntime;
    props.processInitiationViewPageRoute = view.pageRoute;
    props.processInitiationViewContext = view.context;
    props.reportProcessInitiationViewReady = onlyWhileMounted(args.signal, view.reportReady);
    props.reportProcessInitiationViewFailed = onlyWhileMounted(args.signal, view.reportFailed);
  }
  return props;
}

async function startWujieRuntime(
  args: StartRegisteredApplicationArgs,
  runtimeName: string,
  alive: boolean,
  callbacks: StableNavigationRuntimeCallbacks<
    PlatformAccessToken,
    PortalOperationFeedback,
    PortalOperationPageBridge
  >,
) {
  const applicationCode = args.application.appCode;
  throwIfAborted(args.signal);
  let removeAbortListener: () => void = () => undefined;
  const abortPromise = new Promise<never>((_, reject) => {
    const onAbort = () => reject(createAbortError());
    args.signal.addEventListener('abort', onAbort, { once: true });
    removeAbortListener = () => args.signal.removeEventListener('abort', onAbort);
  });
  const startTimeout = timeoutPromise(
    WUJIE_START_TIMEOUT_MS,
    new Error(`微前端应用 ${applicationCode} 启动超时`),
  );
  let rawStart: ReturnType<typeof startApp>;
  try {
    rawStart = startApp({
      name: runtimeName,
      url: resolveTrustedAppUrl(args.application, args.pathname),
      el: args.container,
      sync: false,
      alive,
      props: runtimeProps(args, callbacks),
    });
  } catch (error) {
    removeAbortListener();
    startTimeout.clear();
    throw error;
  }
  const detachedStart = Promise.resolve(rawStart);
  void detachedStart.catch(() => undefined);
  try {
    await Promise.race([
      detachedStart,
      abortPromise,
      startTimeout.promise.then(error => Promise.reject(error)),
    ]);
  } finally {
    removeAbortListener();
    startTimeout.clear();
  }
}

function navigationEntryFingerprint(args: StartRegisteredApplicationArgs) {
  return args.entryFingerprint ?? JSON.stringify([
    args.application.status,
    args.application.entry.embedUrl,
    args.application.entry.basePath,
    args.application.entry.defaultRoute,
  ]);
}

function navigationSessionEpoch(args: StartRegisteredApplicationArgs) {
  return args.sessionEpoch ?? JSON.stringify([
    args.identity.actorPublicId,
    args.identity.tenantPublicId,
    args.identity.platformOperator,
    [...permissionContextForToken(args.accessToken).permissionCodes].sort(),
    [...args.accessToken.scopes].sort(),
  ]);
}

async function startNavigationApplication(args: StartRegisteredApplicationArgs) {
  const applicationCode = args.application.appCode;
  assertNavigationRuntimeStartAllowed(applicationCode);
  const activation = await navigationRuntimeRegistry.activate({
    applicationCode,
    entryFingerprint: navigationEntryFingerprint(args),
    sessionEpoch: navigationSessionEpoch(args),
    callbacks: mountedCallbacks(args),
    start: ({ runtimeName, callbacks }) => startWujieRuntime(
      args,
      runtimeName,
      true,
      callbacks,
    ),
    destroy: runtimeName => destroyRuntimeWithinBudget(
      applicationCode,
      runtimeName,
    ),
  });
  return activation.release;
}

async function startTransientApplication(args: StartRegisteredApplicationArgs) {
  const applicationCode = args.application.appCode;
  const generation = nextApplicationGeneration(applicationCode);
  const runtimeName = await enqueueApplicationOperation(applicationCode, async () => {
    throwIfAborted(args.signal);
    if (!isCurrentGeneration(applicationCode, generation)) throw createAbortError();
    const physicalRuntimeName = resolvePhysicalRuntimeName(
      applicationCode,
      args.mountId,
    );
    try {
      await startWujieRuntime(
        args,
        physicalRuntimeName,
        false,
        mountedCallbacks(args),
      );
    } catch (error) {
      await destroyRuntimeWithinBudget(applicationCode, physicalRuntimeName);
      throw error;
    }
    if (!isCurrentGeneration(applicationCode, generation)) {
      await destroyRuntimeWithinBudget(applicationCode, physicalRuntimeName);
      throw createAbortError();
    }
    return physicalRuntimeName;
  });
  let released = false;
  return () => {
    if (released) return Promise.resolve();
    released = true;
    return enqueueApplicationOperation(applicationCode, async () => {
      if (isCurrentGeneration(applicationCode, generation)) {
        applicationGenerations.set(applicationCode, generation + 1);
      }
      await destroyRuntimeWithinBudget(applicationCode, runtimeName);
    });
  };
}

export async function startRegisteredApplication(
  args: StartRegisteredApplicationArgs,
) {
  return args.runtimePurpose === 'navigation'
    ? startNavigationApplication(args)
    : startTransientApplication(args);
}

/** Raw physical promise: the form registry owns deadline, late completion and reclamation. */
export async function startRegisteredFormApplication(args: Omit<StartRegisteredApplicationArgs,
  'runtimePurpose' | 'mountId' | 'operationRuntime' | 'workflowRuntime' | 'processInitiationViewRuntime'> & {
  runtimeName: string;
  formSessionHost: PlatformFormSessionHostCapability;
}) {
  throwIfAborted(args.signal);
  const baseArgs: StartRegisteredApplicationArgs = { ...args, runtimePurpose: 'transient', mountId: args.formSessionHost.runtimeEpoch };
  const props = runtimeProps(baseArgs, mountedCallbacks(baseArgs));
  props.runtimePurpose = 'form-session';
  props.formSessionHost = args.formSessionHost;
  // Shared primary navigation belongs exclusively to navigation runtimes.
  props.registerPrimaryNavigation = () => undefined;
  props.onPrimaryNavigationChange = () => undefined;
  const raw = Promise.resolve(startApp({ name: args.runtimeName, url: resolveTrustedAppUrl(args.application, args.pathname), el: args.container, sync: false, alive: true, props }));
  void raw.catch(() => undefined);
  await raw;
}

export async function destroyRegisteredFormApplication(runtimeName: string) {
  await destroyApp(runtimeName);
}

export async function invalidateRegisteredNavigationApplication(
  applicationCode: string,
) {
  await navigationRuntimeRegistry.invalidate(applicationCode, async runtimeName => {
    return destroyRuntimeWithinBudget(applicationCode, runtimeName);
  });
}

export async function invalidateAllRegisteredNavigationApplications() {
  await navigationRuntimeRegistry.invalidateAll(async runtimeName => {
    const applicationCode = runtimeName.replace(/^PORTAL_NAV::/, '');
    return destroyRuntimeWithinBudget(applicationCode, runtimeName);
  });
}

export function getRegisteredNavigationSnapshot(applicationCode: string) {
  return navigationRuntimeRegistry.getNavigationSnapshot(applicationCode);
}

export function resetRegisteredNavigationRuntimesForTest() {
  navigationRuntimeRegistry.resetForTest();
  applicationGenerations.clear();
  applicationQueues.clear();
  pendingRuntimeReclaims.clear();
  recoveryBlockedApplications.clear();
}

export function selectRegisteredApplicationPrimaryNavigation(
  application: PortalApplication,
  navigationCode: string,
) {
  bus.$emit(PLATFORM_PRIMARY_NAVIGATION_SELECT_EVENT, {
    applicationCode: application.appCode,
    navigationCode,
  });
}

export function syncRegisteredApplicationRoute(
  application: PortalApplication,
  pathname: string,
) {
  const path = pathname.startsWith(application.entry.basePath)
    ? pathname.slice(application.entry.basePath.length)
    : application.entry.defaultRoute;
  bus.$emit(WUJIE_ROUTE_CHANGE_EVENT, {
    basePath: application.entry.basePath,
    path: path || application.entry.defaultRoute,
  } satisfies WujieRouteChange);
}
